
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";

function formatKRW(n) {
  return `₩${Number(n).toLocaleString("ko-KR")}`;
}

export default function CartPage() {
  const { cartItems, cartLoading, cartError, clearCartError, removeFromCart, clearCart } = useCart();
  const navigate = useNavigate();
  const [pendingAction, setPendingAction] = useState(null);

  const grandTotal = cartItems.reduce((sum, i) => sum + i.total, 0);

  async function handleRemove(id) {
    setPendingAction(`remove-${id}`);
    try {
      await removeFromCart(id);
    } catch {
      // CartContext exposes the user-facing error.
    } finally {
      setPendingAction(null);
    }
  }

  async function handleClear() {
    setPendingAction("clear");
    try {
      await clearCart();
    } catch {
      // CartContext exposes the user-facing error.
    } finally {
      setPendingAction(null);
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 font-poppins">
      <header className="w-full h-[64px] bg-white shadow-sm fixed top-0 z-50 flex items-center px-[60px]">
        <div className="font-bold text-[25px] cursor-pointer" onClick={() => navigate("/")}>ODK</div>
        <div className="ml-6 text-gray-400 text-[14px]">Cart</div>
      </header>

      <div className="pt-[100px] max-w-3xl mx-auto px-6 pb-20">
        <h1 className="text-[28px] font-bold mb-8">🛒 Cart</h1>

        {cartError && (
          <div className="mb-5 flex items-center justify-between gap-4 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-[13px] text-red-500" role="alert">
            <span>{cartError}</span>
            <button type="button" onClick={clearCartError} className="border-0 bg-transparent text-lg leading-none text-red-400" aria-label="Dismiss error">&times;</button>
          </div>
        )}

        {cartLoading ? (
          <div className="space-y-4" aria-label="Loading cart">
            {[0, 1].map((item) => <div key={item} className="h-[110px] animate-pulse rounded-2xl bg-white shadow-sm" />)}
          </div>
        ) : cartItems.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-32 text-gray-400">
            <div className="text-[48px] mb-4">🧺</div>
            <div className="text-[16px]">EMPTY</div>
            <button
              onClick={() => navigate("/")}
              className="mt-6 px-6 py-2.5 rounded-full bg-[#2C2C2A] text-white text-[14px] font-semibold hover:bg-[#2e4485] transition-colors cursor-pointer border-0"
            >
              Go Back
            </button>
          </div>
        ) : (
          <>
            <div className="flex flex-col gap-4">
              {cartItems.map((item) => (
                <div key={item.id} className="bg-white rounded-2xl shadow-sm p-5 flex gap-4 items-center">
                  <img src={item.imgSrc} alt="" className="w-[100px] h-[70px] object-cover rounded-xl" />
                  <div className="flex-1">
                    <div className="font-bold text-[16px] text-gray-900">{item.title}</div>
                    <div className="text-[13px] text-gray-400 mt-1">
                      📅 {item.date} &nbsp;|&nbsp; 👤 {item.persons} person(s)
                    </div>
                    <div className="text-[12px] text-gray-300 mt-0.5">
                      {formatKRW(item.perPerson)} × {item.persons} person(s)
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <div className="text-[18px] font-bold text-gray-900">{formatKRW(item.total)}</div>
                    <button
                      onClick={() => handleRemove(item.id)}
                      disabled={pendingAction === `remove-${item.id}`}
                      className="text-[12px] text-gray-300 hover:text-red-400 transition-colors cursor-pointer bg-transparent border-0 p-0"
                    >
                      {pendingAction === `remove-${item.id}` ? "Removing..." : "Remove"}
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* 합계 */}
            <div className="mt-6 bg-white rounded-2xl shadow-sm p-5">
              <div className="flex justify-between items-center">
                <span className="text-[14px] text-gray-400">Total {cartItems.length}</span>
                <span className="text-[22px] font-bold text-gray-900">{formatKRW(grandTotal)}</span>
              </div>
              <div className="flex gap-3 mt-4">
                <button
                  onClick={handleClear}
                  disabled={pendingAction === "clear"}
                  className="flex-1 py-3 rounded-xl border border-gray-200 text-[14px] text-gray-500 hover:bg-gray-50 transition-colors cursor-pointer bg-white"
                >
                  {pendingAction === "clear" ? "Clearing..." : "Clear All"}
                </button>
                <button
                  onClick={() => navigate("/checkout", {
                    state: {
                      cartItems,
                      total: grandTotal,
                      deposit: cartItems.length * 20000,
                    }
                  })}
                  className="flex-[2] py-3 rounded-xl bg-[#E8876A] text-white text-[14px] font-semibold hover:bg-[#D96F52] transition-colors cursor-pointer border-0"
                >
                  Checkout →
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
